/**
 * Shared stock checks for pharmacy issue-medicine flows (UI + Confirm Issue gating).
 * Does not mutate inventory; deduction remains on confirm/backend.
 */

export const STOCK_STATUS = {
  OK: "OK",
  NOT_IN_INVENTORY: "NOT_IN_INVENTORY",
  OUT_OF_STOCK: "OUT_OF_STOCK",
  INSUFFICIENT_STOCK: "INSUFFICIENT_STOCK",
  INVALID_QTY: "INVALID_QTY",
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getExpiryValue = (item) =>
  item?.expiryDate || item?.expiry_date || item?.expirydate || null;

const getItemMedicineId = (item) => item?.medicineId ?? item?.medicine_id;

/**
 * Normalize medicine names for inventory matching (case/space insensitive).
 * Handles catalog duplicates where prescription medicineId differs from inventory.
 */
export const normalizeMedicineMatchKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .replace(/\s+/g, "");

/**
 * Whether a batch can be used for issue (qty > 0, not expired).
 * @param {object} item
 * @param {{ allowMissingExpiry?: boolean }} options — OPD allows missing expiry; IPD typically does not.
 */
export const isUsableInventoryBatch = (item, { allowMissingExpiry = true } = {}) => {
  if (!item || !(Number(item.quantity) > 0)) return false;

  const expiryDateValue = getExpiryValue(item);
  if (!expiryDateValue) {
    return allowMissingExpiry;
  }

  const expiryDate = new Date(expiryDateValue);
  expiryDate.setHours(0, 0, 0, 0);
  return expiryDate > startOfToday();
};

/**
 * Resolve inventory rows for a prescribed medicine by medicineId and/or name.
 * Name fallback covers duplicate catalog entries (same name, different IDs).
 *
 * @returns {{ matchType: 'id'|'name'|'both'|'none', resolvedMedicineId: number|null, items: object[] }}
 */
export const resolveMedicineInventoryMatch = (
  inventoryList,
  { medicineId, medicineName } = {},
  { allowMissingExpiry = true } = {}
) => {
  const list = Array.isArray(inventoryList) ? inventoryList : [];
  const idNum =
    medicineId != null && medicineId !== "" ? Number(medicineId) : NaN;
  const nameKey = normalizeMedicineMatchKey(medicineName);

  const byId = Number.isFinite(idNum)
    ? list.filter((item) => Number(getItemMedicineId(item)) === idNum)
    : [];
  const byName = nameKey
    ? list.filter(
        (item) =>
          normalizeMedicineMatchKey(item?.name || item?.medicineName) === nameKey
      )
    : [];

  const merged = new Map();
  [...byId, ...byName].forEach((item) => {
    const key = item?.id != null ? `id-${item.id}` : `row-${merged.size}`;
    if (!merged.has(key)) merged.set(key, item);
  });
  const items = [...merged.values()];

  if (items.length === 0) {
    return {
      matchType: "none",
      resolvedMedicineId: Number.isFinite(idNum) ? idNum : null,
      items: [],
    };
  }

  const usable = items.filter((item) =>
    isUsableInventoryBatch(item, { allowMissingExpiry })
  );
  const preferred = usable[0] || items[0];
  const resolvedFromPreferred = Number(getItemMedicineId(preferred));
  const resolvedMedicineId = Number.isFinite(resolvedFromPreferred)
    ? resolvedFromPreferred
    : Number.isFinite(idNum)
      ? idNum
      : null;

  let matchType = "none";
  if (byId.length > 0 && byName.length > 0) matchType = "both";
  else if (byId.length > 0) matchType = "id";
  else if (byName.length > 0) matchType = "name";

  return { matchType, resolvedMedicineId, items };
};

/**
 * Sum of usable stock for a medicine (by id and/or name).
 */
export const getAvailableStockForMedicine = (
  inventoryList,
  medicineId,
  { allowMissingExpiry = true, medicineName } = {}
) => {
  const { items } = resolveMedicineInventoryMatch(
    inventoryList,
    { medicineId, medicineName },
    { allowMissingExpiry }
  );
  return items.reduce((sum, item) => {
    if (!isUsableInventoryBatch(item, { allowMissingExpiry })) return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);
};

/**
 * True if any inventory row exists for this medicine (even expired / qty 0).
 */
export const medicineExistsInInventoryTable = (
  inventoryList,
  medicineId,
  { medicineName } = {}
) => {
  const { items } = resolveMedicineInventoryMatch(inventoryList, {
    medicineId,
    medicineName,
  });
  return items.length > 0;
};

/**
 * Evaluate one line item for issue.
 * @returns {{ status, medicineId, resolvedMedicineId, medicineName, requiredQty, availableQty, message, blocking, matchType }}
 */
export const evaluateMedicineIssueStock = ({
  medicineId,
  medicineName,
  requiredQty,
  inventoryList,
  allowMissingExpiry = true,
}) => {
  const name = medicineName || "Unknown medicine";
  const needed = Number(requiredQty) || 0;

  if (needed <= 0) {
    return {
      status: STOCK_STATUS.INVALID_QTY,
      medicineId,
      resolvedMedicineId: medicineId ?? null,
      medicineName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: "none",
      blocking: true,
      message: `${name}: issue quantity must be greater than 0 (check dosage and duration).`,
    };
  }

  const match = resolveMedicineInventoryMatch(
    inventoryList,
    { medicineId, medicineName: name },
    { allowMissingExpiry }
  );
  const availableQty = match.items.reduce((sum, item) => {
    if (!isUsableInventoryBatch(item, { allowMissingExpiry })) return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);

  if (match.matchType === "none") {
    if (!medicineId) {
      return {
        status: STOCK_STATUS.NOT_IN_INVENTORY,
        medicineId,
        resolvedMedicineId: null,
        medicineName: name,
        requiredQty: needed,
        availableQty: 0,
        matchType: "none",
        blocking: true,
        message: `${name}: medicine is not linked to inventory (missing medicine ID).`,
      };
    }
    return {
      status: STOCK_STATUS.NOT_IN_INVENTORY,
      medicineId,
      resolvedMedicineId: medicineId,
      medicineName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: "none",
      blocking: true,
      message: `${name}: not found in medicine inventory. Add stock for this medicine before issuing.`,
    };
  }

  const resolvedId = match.resolvedMedicineId ?? medicineId;

  if (availableQty <= 0) {
    return {
      status: STOCK_STATUS.OUT_OF_STOCK,
      medicineId,
      resolvedMedicineId: resolvedId,
      medicineName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: match.matchType,
      blocking: true,
      message: `${name}: out of stock (no usable quantity / all batches expired). Required ${needed} unit(s).`,
    };
  }

  if (needed > availableQty) {
    return {
      status: STOCK_STATUS.INSUFFICIENT_STOCK,
      medicineId,
      resolvedMedicineId: resolvedId,
      medicineName: name,
      requiredQty: needed,
      availableQty,
      matchType: match.matchType,
      blocking: true,
      message: `${name}: insufficient stock. Required ${needed} unit(s), available ${availableQty} unit(s).`,
    };
  }

  return {
    status: STOCK_STATUS.OK,
    medicineId,
    resolvedMedicineId: resolvedId,
    medicineName: name,
    requiredQty: needed,
    availableQty,
    matchType: match.matchType,
    blocking: false,
    message: `${name}: OK (${availableQty} available, issuing ${needed}).`,
  };
};

export const hasBlockingStockIssues = (checks = []) =>
  checks.some((c) => c?.blocking);

export const stockStatusLabel = (status) => {
  switch (status) {
    case STOCK_STATUS.OK:
      return "In stock";
    case STOCK_STATUS.NOT_IN_INVENTORY:
      return "Not in inventory";
    case STOCK_STATUS.OUT_OF_STOCK:
      return "Out of stock";
    case STOCK_STATUS.INSUFFICIENT_STOCK:
      return "Low / insufficient stock";
    case STOCK_STATUS.INVALID_QTY:
      return "Invalid quantity";
    default:
      return "Unknown";
  }
};

export const stockStatusSeverity = (status) => {
  if (status === STOCK_STATUS.OK) return "success";
  if (status === STOCK_STATUS.INSUFFICIENT_STOCK) return "warning";
  return "error";
};
